import React, { useState, useEffect, useCallback ,useMemo} from 'react';
import {
  Container,
  Typography,
  Box,
  Stack,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Fade,
  Zoom
} from '@mui/material';
import {
  Search as SearchIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Payment as MoneyIcon
} from '@mui/icons-material';
import axios from 'axios';
import { apiURL ,GetAll} from '../utils/apiCalls';
import PaymentRequestCard from '../components/PaymentRequestCard';
import { toast } from 'react-toastify';
import ProcurementFormModal from '../components/modals/ProcurementFormModal';
import useUserDetails from '../hooks/useUserDetails';
const ProcurementDashboard = () => {
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [notificationChecked, setNotificationChecked] = useState(false);

  const [account, setAccount] = useState({});
  
  const userDetailsStr = sessionStorage.getItem('user_details');
  const userDetailsObj = useMemo(() => {
    try {
      return userDetailsStr ? JSON.parse(userDetailsStr) : {};
    } catch (e) {
      return {};
    }
  }, [userDetailsStr]);

  const getadminDetails = async () => {
    if (!userDetailsObj?.id) return;
    try {
      const response = await GetAll(apiURL + '/api/v1/procurement-admin-users/' + userDetailsObj.id);
      if (response && response.data && response.data.status) {
        const user = response.data.data;
        if (!user.is_kyc) {
          setNotificationChecked(true);
        } else {
          setNotificationChecked(false);
        }
      } else {
        toast.error(response?.data?.message || 'Failed to fetch admin details');
      }
    } catch (error) {
      console.error('Error fetching admin details:', error);
      toast.error('An error occurred while fetching admin details');
    }
  };

  useEffect(() => {
    if (userDetailsObj?.id) {
      getadminDetails();
    }
  }, [userDetailsObj?.id]);
  const fetchPaymentRequests = async () => {
    setLoading(true);
    try {
      const response = await axios({
        method: 'GET',
        url: apiURL + '/api/v1/layout_payments/my/requests/all',
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      
      if (response && response.data && response.data.status) {
        setPaymentRequests(response.data.data || []);
      } else {
        toast.error(response?.data?.message || 'Failed to fetch admin payment requests');
        setPaymentRequests([]);
      }
    } catch (error) {
      console.error('Error fetching admin payment requests:', error);
      toast.error('An error occurred while fetching admin payment requests');
      setPaymentRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPaymentRequests();
    setRefreshing(false);
  };

  const filterRequests = useCallback(() => {
    let filtered = paymentRequests;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(request =>
        request.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }

    setFilteredRequests(filtered);
  }, [paymentRequests, searchTerm, statusFilter]);

  useEffect(() => {
    fetchPaymentRequests();
    checkPaymentNotifications();
  }, []);

  const checkPaymentNotifications = () => {
    // Check for payment success notification
    const paymentSuccess = sessionStorage.getItem('paymentSuccess');
    if (paymentSuccess) {
      const successData = JSON.parse(paymentSuccess);
      sessionStorage.removeItem('paymentSuccess');
      
      // Update the payment request status in local state
      setPaymentRequests(prev => 
        prev.map(request => 
          request.id === successData.paymentRequestId 
            ? { ...request, status: 'completed', transaction_id: `txn_${successData.timestamp}` }
            : request
        )
      );
      
      toast.success('Payment completed successfully!');
    }

    // Check for payment error notification
    const paymentError = sessionStorage.getItem('paymentError');
    if (paymentError) {
      const errorData = JSON.parse(paymentError);
      sessionStorage.removeItem('paymentError');
      
      // Update the payment request status in local state
      setPaymentRequests(prev => 
        prev.map(request => 
          request.id === errorData.paymentRequestId 
            ? { ...request, status: 'failed' }
            : request
        )
      );
      
      toast.error('Payment was cancelled or failed');
    }
  };

  useEffect(() => {
    filterRequests();
  }, [filterRequests]);


  const getStats = () => {
    const total = paymentRequests.length;
    const pending = paymentRequests.filter(r => r.status === 'pending').length;
    const completed = paymentRequests.filter(r => r.status === 'completed' || r.status === 'paid').length;
    const totalAmount = paymentRequests.reduce((sum, r) => sum + (r.amount || 0), 0);
    const pendingAmount = paymentRequests
      .filter(r => r.status === 'pending')
      .reduce((sum, r) => sum + (r.amount || 0), 0);

    return { total, pending, completed, totalAmount, pendingAmount };
  };


  const stats = getStats();
  console.log(account)
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
            </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}

      <ProcurementFormModal open={notificationChecked} onClose={() => { setNotificationChecked(false); }} />
      <Fade in timeout={600}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary">
                Layout Payments
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Payment requests raised by admin
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Stack>
        </Box>
      </Fade>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={800}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <PaymentIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Requests
                </Typography>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={900}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <TrendingUpIcon sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {stats.pending}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Payments
                </Typography>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={1000}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <ReceiptIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {stats.completed}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Zoom>
      </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={1100}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <MoneyIcon sx={{ fontSize: 40, color: '#9c27b0', mb: 1 }} />
                <Typography variant="h4" fontWeight="bold" color="secondary.main">
                  ₹{stats.pendingAmount.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Amount
      </Typography>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              fullWidth
                variant="outlined"
              placeholder="Search payment requests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 400 }}
            />
            
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`Total: ${stats.total}`}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`Pending: ${stats.pending}`}
                color="warning"
                variant="outlined"
              />
              <Chip
                label={`Completed: ${stats.completed}`}
                color="success"
                variant="outlined"
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Payment Requests List */}
      {filteredRequests.length === 0 ? (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <PaymentIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              {paymentRequests.length === 0 ? 'No Admin Payment Requests' : 'No Matching Requests'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {paymentRequests.length === 0 
                ? 'No payment requests have been raised by admin yet.'
                : 'Try adjusting your search or filter criteria.'
              }
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box>
          {filteredRequests.map((request, index) => (
            <Fade in timeout={600 + (index * 100)} key={request.id}>
              <Box>
                <PaymentRequestCard
                  paymentRequest={request}
                />
                </Box>
              </Fade>
            ))}
          </Box>
      )}
    </Container>
  );
};

export default ProcurementDashboard;
